import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FilePdfOutlined } from "@ant-design/icons";
import { statusLabel } from "@wms/domain";
import type { ActionMeta, ActionResult, Workflow } from "@wms/domain";
import { App, Button, Descriptions, Divider, Modal, Popconfirm, Space, Table, Tag, Tooltip, Typography } from "antd";
import type { ReactNode } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, describeError } from "../api/client";
import { visibleActions } from "./actionGroups";
import { outcomeUnknown } from "./formProblem";
import { openPrintForm } from "../api/print";
import { kindOfEntity, legacyFormOf, printOpensViewer } from "../api/renditions";
import { useAuthStore } from "../auth/store";
import { DocumentViewer } from "./DocumentViewer";
import { FormActionModal } from "./FormActionModal";
import { HelpHint } from "./HelpHint";
import { LabelsModal } from "./LabelsModal";
import { LinesActionModal } from "./LinesActionModal";
import { PreviewConfirmModal } from "./PreviewConfirmModal";

/** Kinds this build can open; a newer server may send others to a tab left open across a release. */
const KNOWN_KINDS = new Set<string>(["simple", "lines", "form", "print", "labels"]);

interface Props {
  entity: string;
  id: string;
  status?: string;
  actions: ActionMeta[];
  disabled?: boolean;
  /** Current row, used for default_from of form actions. */
  row?: Record<string, unknown>;
  /** Permission prefix of the entity; actions without their own permission need <prefix>.write. */
  permission?: string;
  /** UX-forms section 12: availability, reasons and the one recommended step from GET /{entity}/{id}/workflow. */
  workflow?: Workflow | null;
  /** After a successful action: the card refetches the workflow and the row. */
  onDone?: () => void;
  /** ТЗ «Понятные шаги» (UX1-019): only these codes — the actions of one stage; the rest stay in «Все действия». */
  only?: string[];
  /** Stepwise layout: large buttons, and the main one says under itself what it will change (UX1-006 (5), (6)). */
  stepwise?: boolean;
}

/** What is posted by one press: the action and, for a preview with a choice of rows, the chosen keys (UX1-044). */
interface Run {
  action: ActionMeta;
  body?: Record<string, unknown>;
}

const STATUS_WORDS = /^[a-z_]+$/;

// the stable order lives with the grouping of the panel (one function for both ways of showing the actions)
export { orderActions } from "./actionGroups";

interface RunnerProps {
  entity: string;
  id: string;
  row?: Record<string, unknown>;
  permission?: string;
  workflow?: Workflow | null;
  onDone?: () => void;
  /**
   * UXW-59 (option «Панель действий»): a lost answer of an action is checked against the record before anything
   * else is offered — «Проверяем результат…», never a bare «нажмите ещё раз».
   */
  verify?: boolean;
}

/** One press of an action and everything it may open: the same handlers for the row of buttons and the panel. */
export interface ActionRunner {
  allowed: (action: ActionMeta) => boolean;
  /** R41: why a button cannot be pressed (rights, the assignee of a task, the reason of the workflow). */
  blockedReason: (action: ActionMeta) => string | null;
  /** UX1-042: the server's subject words for the button, else its label. */
  labelOf: (action: ActionMeta) => string;
  /** What a press does: opens the dialog of its kind, or posts a simple action without a confirmation. */
  start: (action: ActionMeta) => void;
  /** «Да» of the confirmation of a simple action. */
  confirmRun: (action: ActionMeta) => void;
  pending: (code: string) => boolean;
  /** «Документы и печать»: the viewer of the stored PDF of the document. */
  openDocuments: () => void;
  showDocs: boolean;
  /** A dialog (other than the one-time password) is open: the row stays mounted to keep it. */
  dialogOpen: boolean;
  /** The dialogs, drawn once at a stable place outside the buttons (R34). */
  dialogs: ReactNode;
}

/**
 * The single mechanism behind the buttons of a document (UXW-47/49): the right, the reason, the endpoint by the stable
 * code, the dialogs of every kind with their fields, previews, request keys and callbacks. DocumentActions draws it as
 * the row of HEAD; ActionPanel draws the same runner in groups — the handlers are never copied.
 */
export function useActionRunner({ entity, id, row, permission, workflow, onDone, verify }: RunnerProps): ActionRunner {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const can = useAuthStore((s) => s.can);
  const allowed = (action: ActionMeta) => can(action.permission ?? `${permission ?? entity}.write`);
  const me = useAuthStore((s) => s.me);
  /** R41: a disabled button says why, before anyone presses it. */
  const blockedReason = (action: ActionMeta): string | null => {
    if (!allowed(action)) return "У вашей роли нет права на это действие. Обратитесь к руководителю или администратору.";
    if (entity === "tasks" && row) {
      const assignee = row.assignee_id ? String(row.assignee_id) : null;
      const mine = !assignee || assignee === me?.user.id || !!me?.user.is_superuser;
      if (!mine && ["take", "start", "release"].includes(action.code)) return "Задание назначено другому исполнителю";
    }
    // UI-22: a future step of the path waits with the real reason from the server
    const state = workflow?.actions[action.code];
    if (state && (state.state === "disabled" || state.state === "forbidden") && state.reason) return state.reason;
    return null;
  };
  const { message } = App.useApp();
  const [linesAction, setLinesAction] = useState<ActionMeta | null>(null);
  const [formAction, setFormAction] = useState<ActionMeta | null>(null);
  const [previewAction, setPreviewAction] = useState<ActionMeta | null>(null);
  const [labelsAction, setLabelsAction] = useState<ActionMeta | null>(null);
  const [planResult, setPlanResult] = useState<{ action: ActionMeta; data: ActionResult } | null>(null);
  // the client flow of 30.10.2026: «Сбросить пароль» answered only «выполнено» and the new temporary password was lost
  const [secret, setSecret] = useState<{ action: ActionMeta; data: ActionResult } | null>(null);
  // KZ-DOC UI03: print buttons and «Документы и печать» open one viewer of the stored PDF; the old HTML form stays
  // reachable from it (TMP24) and remains the only path for cards without a document kind (cell labels of a warehouse)
  const [viewer, setViewer] = useState<{ legacyForm: string | null; legacyPath: string | null } | null>(null);
  const docKind = kindOfEntity(entity);
  const printKind = kindOfEntity(entity, true);
  const showDocs = !!docKind && can("printing.print");

  const run = useMutation({
    // UX1-008: the endpoint is the stable code of the action — never its label, its place or a step number
    mutationFn: ({ action, body }: Run) => api.post<ActionResult>(`/${entity}/${id}/${action.code}`, body),
    onSuccess: (data, { action }) => {
      setPreviewAction(null);
      queryClient.invalidateQueries({ queryKey: [entity] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      onDone?.();
      if (data?.temporary_password) {
        setSecret({ action, data });
        return;
      }
      // B10: planning says what it created and why the rest was not planned, instead of an empty «Готово»
      if (typeof data?.created === "number" || data?.next_step || data?.open_path) {
        setPlanResult({ action, data });
        return;
      }
      const status = data?.status;
      // FULL0211: the server names statuses the general dictionary does not know («В карантине», «Проведено»)
      if (status && STATUS_WORDS.test(String(status))) message.success(`${action.label}: статус «${data?.status_label ?? statusLabel(status)}»`);
      else message.success(`${action.label}: выполнено`);
    },
    onError: (e, { action }) => {
      if (verify && outcomeUnknown(e)) {
        void verifyResult(action);
        return;
      }
      message.error(describeError(e), 6);
    },
  });

  /**
   * UXW-59: the answer is lost, the action may have been done. The record is read again: a changed status or time of
   * change means it was done (the card follows); an unchanged record means it was not; no network — the result is
   * unknown and nothing is offered to repeat until the card can be read.
   */
  const verifyResult = async (action: ActionMeta) => {
    const key = `verify-${entity}-${id}-${action.code}`;
    const name = labelOf(action);
    message.loading({ key, content: `«${name}»: ответ сервера не получен. Проверяем результат…`, duration: 0 });
    try {
      const fresh = await api.get<Record<string, unknown>>(`/${entity}/${id}`);
      queryClient.setQueryData([entity, id], fresh);
      onDone?.();
      const changed = String(fresh.status ?? "") !== String(row?.status ?? "") || (!!fresh.updated_at && String(fresh.updated_at) !== String(row?.updated_at ?? ""));
      if (changed) {
        const status = fresh.status ? ` — статус «${statusLabel(fresh.status)}»` : "";
        message.success({ key, content: `«${name}»: выполнено, документ изменился${status}. Повторять не нужно.`, duration: 8 });
      } else {
        message.warning({ key, content: `«${name}»: документ не изменился — действие не выполнено. Проверьте связь и нажмите кнопку снова.`, duration: 10 });
      }
    } catch {
      message.warning({
        key,
        content: `«${name}»: связи с сервером нет, результат пока неизвестен. Не повторяйте действие: когда связь появится, обновите карточку и проверьте статус и историю.`,
        duration: 12,
      });
    }
  };

  /** UX1-042: the server may word the button for its subject («Утвердить 7 товаров»); the code stays the same. */
  const labelOf = (action: ActionMeta) => workflow?.actions[action.code]?.label ?? action.label;
  const closeDialog = (setter: (v: null) => void) => () => {
    setter(null);
    onDone?.();
  };
  const pending = (code: string) => run.isPending && run.variables?.action.code === code;
  const start = (action: ActionMeta) => {
    // «Кликнуть двадцать раз по проведению»: a press while the same action is posted adds nothing
    if (pending(action.code)) return;
    // 29.10.2026: an unknown kind went to the preview dialog and showed a bare «Not Found»
    if (!KNOWN_KINDS.has(action.kind)) message.warning(`«${action.label}» есть только в новой версии кабинета, а эта вкладка открыта со старой. Обновите страницу (Ctrl+R или ⌘R).`);
    else if (action.kind === "lines") setLinesAction(action);
    else if (action.kind === "form") setFormAction(action);
    else if (action.kind === "labels") setLabelsAction(action);
    else if (action.kind === "print" && printKind && printOpensViewer(entity, action.code)) setViewer({ legacyForm: legacyFormOf(action.code), legacyPath: `/${action.code}/${id}` });
    else if (action.kind === "print") openPrintForm(`/${action.code}/${id}`).catch((e) => message.error(describeError(e)));
    else if (action.preview) setPreviewAction(action);
    else if (!action.confirm) run.mutate({ action });
  };
  const confirmRun = (action: ActionMeta) => {
    if (!pending(action.code)) run.mutate({ action });
  };

  const dialogs = (
    <>
      {secret && (
        <Modal
          title="Новый временный пароль"
          open
          onCancel={() => setSecret(null)}
          footer={<Button type="primary" onClick={() => setSecret(null)} data-testid="temporary-password-done">Пароль передан</Button>}
          maskClosable={false}
          destroyOnHidden
        >
          <Typography.Paragraph>
            Передайте клиенту логин и пароль. После закрытия окна пароль больше не покажется — при необходимости
            сбросьте его ещё раз.
          </Typography.Paragraph>
          <Descriptions column={1} size="small" bordered>
            {secret.data.username && <Descriptions.Item label="Логин">{secret.data.username}</Descriptions.Item>}
            <Descriptions.Item label="Временный пароль">
              <Typography.Text code copyable data-testid="temporary-password">{secret.data.temporary_password}</Typography.Text>
            </Descriptions.Item>
            {entity === "client_users" && (
              <Descriptions.Item label="Вход в кабинет">
                <Typography.Text copyable>{`${window.location.origin}/portal/login`}</Typography.Text>
              </Descriptions.Item>
            )}
          </Descriptions>
          <Typography.Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }}>
            {secret.data.note ?? "При первом входе система попросит задать свой пароль."}
          </Typography.Paragraph>
        </Modal>
      )}
      {planResult && (
        <Modal
          title={`${labelOf(planResult.action)}: результат`}
          open
          onCancel={() => setPlanResult(null)}
          footer={
            <Space>
              {planResult.data.open_path && (
                // UX1-044: the record the action created («Пересчитать оставшиеся» → the new draft) opens from here
                <Button
                  type="primary"
                  data-testid="open-created"
                  onClick={() => {
                    const path = planResult.data.open_path!;
                    setPlanResult(null);
                    navigate(path);
                  }}
                >
                  Открыть {planResult.data.numbers?.[0] ?? "созданный документ"}
                </Button>
              )}
              <Button type={planResult.data.open_path ? "default" : "primary"} onClick={() => setPlanResult(null)}>Понятно</Button>
            </Space>
          }
          width={720}
          destroyOnHidden
        >
          {typeof planResult.data.created === "number" && (
            <Typography.Paragraph>
              {planResult.data.created_label ?? "Создано заданий"}: <b>{planResult.data.created}</b>
              {planResult.data.numbers?.length ? <> — {planResult.data.numbers.map((n) => <Tag key={n}>{n}</Tag>)}</> : null}
              {planResult.data.created === 0 && !planResult.data.short?.length && !planResult.data.next_step ? " — всё уже спланировано раньше, повтор ничего не дублирует." : null}
            </Typography.Paragraph>
          )}
          {planResult.data.next_step && <Typography.Paragraph>{planResult.data.next_step}</Typography.Paragraph>}
          {planResult.data.short && planResult.data.short.length > 0 && (
            <>
              <Typography.Text strong type="danger">Не спланировано</Typography.Text>
              <Table
                size="small"
                style={{ marginTop: 8 }}
                rowKey={(r, i) => String(r.line_no ?? r.storage_object_id ?? i)}
                pagination={false}
                dataSource={planResult.data.short}
                columns={[
                  { title: "Строка", dataIndex: "line_no", width: 80, render: (v: number | undefined) => v ?? "—" },
                  { title: "Не хватает", dataIndex: "short", width: 110, render: (v: string | undefined) => v ?? "—" },
                  { title: "Почему", dataIndex: "reason", render: (v: string | undefined) => v ?? "причина не указана" },
                ]}
              />
            </>
          )}
        </Modal>
      )}
      {previewAction && (
        <PreviewConfirmModal
          entity={entity}
          id={id}
          action={previewAction}
          pending={run.isPending}
          onConfirm={(body) => run.mutate({ action: previewAction, body })}
          onClose={() => setPreviewAction(null)}
        />
      )}
      {linesAction && <LinesActionModal entity={entity} id={id} action={linesAction} row={row} verify={verify} onClose={closeDialog(setLinesAction)} />}
      {labelsAction && <LabelsModal key={`labels-${labelsAction.code}`} entity={entity} id={id} action={labelsAction} row={row} onClose={() => setLabelsAction(null)} />}
      {formAction && <FormActionModal key={`form-${formAction.code}`} entity={entity} id={id} action={formAction} row={row} verify={verify} onClose={closeDialog(setFormAction)} />}
      {viewer && printKind && (
        <DocumentViewer
          open
          onClose={() => setViewer(null)}
          kind={printKind}
          documentId={id}
          legacyForm={viewer.legacyForm}
          legacyPath={viewer.legacyPath}
          title={typeof row?.number === "string" ? row.number : undefined}
        />
      )}
    </>
  );
  return {
    allowed,
    blockedReason,
    labelOf,
    start,
    confirmRun,
    pending,
    openDocuments: () => setViewer({ legacyForm: null, legacyPath: null }),
    showDocs,
    dialogOpen: !!(formAction || linesAction || planResult || previewAction || labelsAction || viewer),
    dialogs,
  };
}

/** Buttons for document actions: simple ones POST immediately, "lines" ones open a modal. */
export function DocumentActions({ entity, id, status, actions, disabled, row, permission, workflow, onDone, only, stepwise }: Props) {
  const runner = useActionRunner({ entity, id, row, permission, workflow, onDone });
  const { blockedReason, labelOf, start, confirmRun, pending, openDocuments, showDocs } = runner;
  // with a workflow the future steps of the path are shown disabled with their reason, in the same stable order
  const all = visibleActions(actions, status, row, workflow);
  // UX1-019: the stage shows its own actions; every other allowed action stays reachable from «Все действия»
  const visible = only ? all.filter((a) => only.includes(a.code)) : all;
  const docsButton = showDocs && !only;
  // the main action of the stage: the recommended one, else the single one it has
  const focal = stepwise ? (visible.find((a) => workflow?.actions[a.code]?.recommended) ?? (visible.length === 1 ? visible[0] : null)) : null;
  // an open dialog keeps its result on screen even when the new status leaves no buttons
  if (!visible.length && !docsButton && !runner.dialogOpen) return null;

  const described = visible.filter((a) => a.help);
  // UX3010-03: print, labels and help are actions of their own, not steps of the path — with the workflow they stand
  // apart after a thin divider and are never locked by it
  const firstAside = workflow ? visible.findIndex((a, i) => i > 0 && (a.group === "print" || a.group === "assist")) : -1;

  return (
    <>
      <Space wrap style={{ maxWidth: "100%" }} align="center">
      {visible.map((action, index) => {
        const reason = blockedReason(action);
        const aside = index === firstAside ? <Divider key={`aside-${action.code}`} type="vertical" style={{ height: 24, marginInline: 2 }} /> : null;
        const recommended = !!workflow?.actions[action.code]?.recommended;
        const primary = workflow
          ? recommended || (stepwise && focal === action && !action.danger)
          : action.kind !== "simple" && action.kind !== "print" && action.kind !== "labels" && !action.danger;
        const text = labelOf(action);
        const button = (
          <Button
            key={action.code}
            className="wms-wrap-button"
            data-testid={`action-${action.code}`}
            data-recommended={recommended || undefined}
            danger={action.danger}
            type={primary ? "primary" : "default"}
            size={stepwise ? "large" : undefined}
            disabled={disabled || !!reason}
            loading={pending(action.code)}
            onClick={() => start(action)}
          >
            {recommended ? <Tag color="blue" style={{ marginRight: 6, marginInlineEnd: 6 }}>Следующий шаг</Tag> : null}
            {text}
          </Button>
        );
        if (reason && !disabled) {
          // UI-22: the reason is readable on hover, focus and tap through the wrapper, not the dead button;
          // in the stepwise card the reason is also written out under the button (UX1-022: not only on hover)
          return [
            aside,
            <Tooltip key={action.code} title={action.help ? `${reason}. ${action.help}` : reason}>
              <span style={{ display: "inline-block", maxWidth: "100%" }} tabIndex={0} aria-label={`${text}: ${reason}`}>
                {button}
                {stepwise ? <div className="wms-step-outcome" data-testid={`reason-${action.code}`}>{reason}</div> : null}
              </span>
            </Tooltip>,
          ];
        }
        const wrapped = action.help ? (
          <Tooltip key={action.code} title={action.help} mouseEnterDelay={0.4}>
            <span style={{ display: "inline-block" }}>{button}</span>
          </Tooltip>
        ) : (
          button
        );
        return [
          aside,
          action.confirm && action.kind === "simple" && !action.preview ? (
            <Popconfirm key={action.code} title={`${text}?`} description={action.outcome ?? action.help ?? undefined} okText="Да" cancelText="Отмена" onConfirm={() => confirmRun(action)}>
              {wrapped}
            </Popconfirm>
          ) : (
            wrapped
          ),
        ];
      })}
      {docsButton && (
        <Button
          key="documents-and-print"
          className="wms-wrap-button"
          icon={<FilePdfOutlined />}
          data-testid="documents-and-print"
          onClick={openDocuments}
        >
          Документы и печать
        </Button>
      )}
      {described.length > 0 && !only && (
        // UI-20: on a touchscreen there is no hover — one «(?)» lists what every button does
        <HelpHint
          subject="Что делают кнопки"
          text={
            <ul style={{ margin: 0, paddingLeft: 18, maxHeight: 320, overflow: "auto" }}>
              {described.map((a) => (
                <li key={a.code} style={{ marginBottom: 4 }}>
                  <b>{a.label}</b> — {a.help}
                </li>
              ))}
            </ul>
          }
        />
      )}
      </Space>
      {stepwise && focal && (focal.outcome || focal.help) && !blockedReason(focal) && (
        // UX1-006 (6): what the press will change, in words, under the main button — not only in a tooltip
        <div className="wms-step-outcome" data-testid="step-outcome">
          Что изменится: {focal.outcome ?? focal.help}
        </div>
      )}
      {/* R34: the dialogs live outside the button row — a status change that adds or removes buttons no longer
          remounts an open dialog and wipes its result */}
      {runner.dialogs}
    </>
  );
}
